import React, { useEffect, useState, Fragment, useContext } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { AppContext } from '../../App/AppContext';
import { UnsignedApproval } from '.';
import defaults from '../../../translations/en.json';
import { WA } from '../../../constants';
import { formatMMDDYY, notesHavePSO } from '../../../utils';
import { getUserFilter } from '../../utils/utils';

const {
  noUnsignedOrders,
  Loading,
  ReceivedByCredentials,
  SignedByCredentials,
} = defaults;

const UnsignedApprovalContainer = styled.div`
  margin-right: 10px;
  margin-left: 10px;
`;

const NoApprovalsContainer = styled.div`
  margin: 10px;
`;

export const ApprovalContainer = (props) => {
  const {
    patientNumber,
    workflow,
    observation,
    theme,
    hospiceBin,
    physicianPersonId,
    // OP-31894: when set, only this unsigned order is shown (opened from the "By Order" view)
    approvalId,
    userId,
    userNumber,
    printView,
    firstName,
    lastName,
    userCredentials,
  } = props;

  const { formatMessage } = useIntl();

  // pulls from the provider in index.js
  const { isDoctorRoleGroup } = useContext(AppContext);

  const [loading, setLoading] = useState(true);
  const [ppn, setPpn] = useState(null);
  const [pso, setPso] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [approvalIndex, setApprovalIndex] = useState(0);
  const [numOfApprovals, setNumOfApprovals] = useState(0);
  const [approvedNum, setApprovedNum] = useState(0);
  const [fetchingData, setFetchingData] = useState(false);

  // on first load of the modal get workflow approvals.
  useEffect(() => {
    if (!approvals) {
      // eslint-disable-next-line func-names
      (async function () {
        const workflowResponse = await workflow.getPatientVAApprovals(
          patientNumber,
          hospiceBin,
        );
        if (workflowResponse.length > 0) {
          const filterByUser = !isDoctorRoleGroup || getUserFilter();
          const filteredByPhys = filterByUser
            ? filterApprovalsByPhys(workflowResponse)
            : workflowResponse;
          const filteredResponse =
            approvalId !== undefined && approvalId !== null
              ? filterApprovalsById(filteredByPhys)
              : filteredByPhys;
          setApprovals(filteredResponse);
          setNumOfApprovals(filteredResponse.length);
          // OP-31894: the single order may have been signed since the grid loaded
          if (filteredResponse.length === 0) setLoading(false);
        } else if (workflowResponse.length === 0) setLoading(false);
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    } else {
      setLoading(false);
    }
  }, []);

  // when approvals or approvedNum state is updated, check to see if the PPN at index 0 has already been fetched.
  // If not get it and add to the workflow approval.
  useEffect(() => {
    // eslint-disable-next-line func-names
    (async function () {
      if (approvals !== null) {
        if (approvals[approvalIndex]) {
          setFetchingData(true);
          if (!approvals[approvalIndex].note) {
            // if the approval does not have a note, get the progress notes assoicated with that approval
            const progressNotes = await observation.getProgressNotesById(
              approvals[approvalIndex].entityid,
              hospiceBin,
            );
            // if the notes exist..
            if (!(progressNotes.status && progressNotes.status === 404)) {
              const psoEntityKey = notesHavePSO(
                approvals[approvalIndex],
                progressNotes,
              );
              if (psoEntityKey) {
                const patientStandingOrder = await observation.getPatientStandingOrderByIdV2(
                  psoEntityKey,
                  hospiceBin,
                );
                setPso(patientStandingOrder);
              }
              setPpn(progressNotes);
            } else {
              setPpn(undefined);
            }
          } else {
            setPpn(approvals[approvalIndex].note);
          }
          setLoading(false);
          setFetchingData(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, approvedNum, approvalIndex]);

  const filterApprovalsByPhys = (unfilteredApprovals) =>
    unfilteredApprovals.filter(
      (approval) =>
        approval.physicianpersonid === physicianPersonId ||
        approval.physicianpersonnumber === physicianPersonId,
    );

  const filterApprovalsById = (unfilteredApprovals) =>
    unfilteredApprovals.filter(
      (approval) => String(approval.id) === String(approvalId),
    );

  const handleNext = () => {
    setApprovalIndex(approvalIndex + 1);
    setPpn(null);
    setPso(null);
  };

  const handlePrev = () => {
    setApprovalIndex(approvalIndex - 1);
    setPpn(null);
    setPso(null);
  };

  const handleApprove = async (note, date, certified) => {
    setFetchingData(true);
    const signedDate = formatMMDDYY(
      date || new Date(),
      approvals[approvalIndex].timezone,
    );
    const postDate = formatPostDate(date || new Date());

    // if user can sign, append an 'electronically signed by...', else record hard copy sig.
    const signature = certified
      ? formatMessage(SignedByCredentials, {
          firstName,
          lastName,
          userCredentials,
          signedDate,
        })
      : formatMessage(ReceivedByCredentials, {
          firstName,
          lastName,
          userCredentials,
          signedDate,
        });

    // eslint-disable-next-line no-nested-ternary
    const signedApprovalNote = note.concat(` - ${signature}`);

    approvals[approvalIndex].approvalstatusshortname = WA;
    approvals[approvalIndex].physiciannote = signedApprovalNote;
    approvals[approvalIndex].recordedbypersonid = userId.toString();
    approvals[approvalIndex].signaturedatetime = postDate;

    if (ppn.note) {
      ppn.note = ppn.note.concat(
        note
          ? `\n\n **** ${signature} - ${note} ****`
          : `\n\n **** ${signature} ****`,
      );
    }
    ppn.modifieddatetime = `${postDate}.0`;
    ppn.modifiedpersonid = userId.toString();
    ppn.modifiedpersonnumber = userNumber.toString();
    const approvalDTO = approvals[approvalIndex];
    approvalDTO.note = ppn.note;
    await workflow.postSignedVerbalApproval(approvalDTO, hospiceBin, 'PUT');
    if (!approvals[approvalIndex + 1]?.note) {
      setPpn(null);
      setPso(null);
    }
    setApprovals(
      approvals.filter((_element, index) => index !== approvalIndex),
    );
    setNumOfApprovals(numOfApprovals - 1);
    setApprovedNum(approvedNum + 1);
    setFetchingData(false);
  };

  return (
    <Fragment>
      {loading ? (
        <Fragment>{Loading}</Fragment>
      ) : (
        <UnsignedApprovalContainer
          style={{ backgroundColor: theme.palette.background.header }}
        >
          {approvals && approvals.length > 0 ? (
            <UnsignedApproval
              approval={approvals[approvalIndex]}
              index={approvalIndex}
              ppn={ppn}
              pso={pso}
              numOfApprovals={numOfApprovals}
              approvedNum={approvedNum}
              handleNext={handleNext}
              handlePrev={handlePrev}
              handleApprove={handleApprove}
              theme={theme}
              fetchingData={fetchingData}
              isDoctorRoleGroup={isDoctorRoleGroup}
              printView={printView}
            />
          ) : (
            <NoApprovalsContainer>{noUnsignedOrders}</NoApprovalsContainer>
          )}
        </UnsignedApprovalContainer>
      )}
    </Fragment>
  );
};
